import { blue, cyan, green, grey, lightBlue, pink, red } from "@mui/material/colors";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import React from "react";

const colours = {
  primaryMain: "#00AAe4",
  primaryContrastText: "#fff",
  secondaryMain: grey[300],
  secondaryContrastText: "#000",
  eaPurpleMain: "#220639",
  eaPurpleContrastText: "#fff",
  darkBackgroundMain: grey["A400"],
  darkBackgroundLight: "#363636",
  darkBackgroundContrastText: grey[300],
  lightBackgroundMain: grey[200],
  lightBackgroundLight: grey[50],
  lightBackgroundContrastText: grey[600],
  successMain: "#1C9D5C",
  warningMain: "#FAB722",
  redStatusMain: "#E51E3D",
  redStatusContrastText: grey[100],
  amberStatusMain: "#FAB722",
  amberStatusContrastText: grey[800],
  greenStatusMain: "#1C9D5C",
  greenStatusContrastText: grey[100],
  lightBlueMain: cyan[200],
  lightBlueContrastText: grey[800],
  lightBlueLight: cyan[100],
  lightBlueDark: cyan[300],
  lightPinkMain: pink[200],
  lightPinkContrastText: grey[800],
  lightPinkLight: pink[100],
  lightPinkDark: pink[300],
  lightGreenMain: green[200],
  lightGreenContrastText: grey[800],
  lightGreenLight: green[100],
  lightGreenDark: green[300],
  tabsMain: lightBlue["A200"],
  tabsContrastText: grey[500],
  tabsLight: lightBlue["A100"],
  tabsDark: lightBlue["A700"],
  red: red[900],
  blue: blue[700],
  grey: grey[700],
};

declare module "@mui/styles/defaultTheme" {
  interface DefaultTheme extends Theme {}
}

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    eaPurple: Palette["primary"];
    darkBackground: Palette["primary"];
    lightBackground: Palette["primary"];
    redStatus: Palette["primary"];
    amberStatus: Palette["primary"];
    greenStatus: Palette["primary"];
    lightBlue: Palette["primary"];
    lightPink: Palette["primary"];
    lightGreen: Palette["primary"];
    tabs: Palette["primary"];
    red: Palette["primary"];
    blue: Palette["primary"];
    midGrey: Palette["primary"];
  }
  interface PaletteOptions {
    eaPurple: PaletteOptions["primary"];
    darkBackground: PaletteOptions["primary"];
    lightBackground: PaletteOptions["primary"];
    redStatus: PaletteOptions["primary"];
    amberStatus: PaletteOptions["primary"];
    greenStatus: PaletteOptions["primary"];
    lightBlue: Palette["primary"];
    lightPink: Palette["primary"];
    lightGreen: Palette["primary"];
    tabs: Palette["primary"];
    red: PaletteOptions["primary"];
    blue: PaletteOptions["primary"];
    midGrey: PaletteOptions["primary"];
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    lightBlue: true;
    lightPink: true;
    lightGreen: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    lightBlue: true;
    lightPink: true;
    lightGreen: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    eaPurple: Palette["primary"];
    darkBackground: Palette["primary"];
    lightBackground: Palette["primary"];
    redStatus: Palette["primary"];
    amberStatus: Palette["primary"];
    greenStatus: Palette["primary"];
    lightBlue: Palette["primary"];
    lightPink: Palette["primary"];
    lightGreen: Palette["primary"];
    tabs: Palette["primary"];
  }
  interface PaletteOptions {
    eaPurple: PaletteOptions["primary"];
    darkBackground: PaletteOptions["primary"];
    lightBackground: PaletteOptions["primary"];
    redStatus: PaletteOptions["primary"];
    amberStatus: PaletteOptions["primary"];
    greenStatus: PaletteOptions["primary"];
    lightBlue: Palette["primary"];
    lightPink: Palette["primary"];
    lightGreen: Palette["primary"];
    tabs: Palette["primary"];
  }
}

const theme = createTheme({
  zIndex: {
    drawer: 900,
  },
  palette: {
    primary: {
      main: colours.primaryMain,
      contrastText: colours.primaryContrastText,
    },
    secondary: {
      main: colours.secondaryMain,
      contrastText: colours.secondaryContrastText,
    },
    eaPurple: {
      main: colours.eaPurpleMain,
      contrastText: colours.eaPurpleContrastText,
    },
    darkBackground: {
      main: colours.darkBackgroundMain,
      light: colours.darkBackgroundLight,
      contrastText: colours.darkBackgroundContrastText,
    },
    lightBackground: {
      main: colours.lightBackgroundMain,
      light: colours.lightBackgroundLight,
      contrastText: colours.lightBackgroundContrastText,
    },
    success: {
      main: colours.successMain,
    },
    warning: {
      main: colours.warningMain,
    },
    error: {
      main: colours.redStatusMain,
    },
    redStatus: {
      main: colours.redStatusMain,
      contrastText: colours.redStatusContrastText,
    },
    amberStatus: {
      main: colours.amberStatusMain,
      contrastText: colours.amberStatusContrastText,
    },
    greenStatus: {
      main: colours.greenStatusMain,
      contrastText: colours.greenStatusContrastText,
    },
    lightBlue: {
      main: colours.lightBlueMain,
      contrastText: colours.lightBlueContrastText,
      light: colours.lightBlueLight,
      dark: colours.lightBlueDark,
    },
    lightPink: {
      main: colours.lightPinkMain,
      contrastText: colours.lightPinkContrastText,
      light: colours.lightPinkLight,
      dark: colours.lightPinkDark,
    },
    lightGreen: {
      main: colours.lightGreenMain,
      contrastText: colours.lightGreenContrastText,
      light: colours.lightGreenLight,
      dark: colours.lightGreenDark,
    },
    tabs: {
      main: colours.tabsMain,
      contrastText: colours.tabsContrastText,
      light: colours.tabsLight,
      dark: colours.tabsDark,
    },
    red: {
      main: colours.red,
    },
    blue: {
      main: colours.blue,
    },
    midGrey: {
      main: colours.grey,
      dark: colours.lightGreenContrastText,
    },
  },
  typography: {
    fontFamily: [
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "capitalize",
          fontWeight: 400,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: "h6" },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: "#00aae4 !important", //using !important because MUI is trying to override the colour with default "indigo" after navigating away from the page and back again
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: "2px",
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          background: "inherit",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          background: "#fff",
          "&$selected": {
            backgroundColor: "#E3F2FD",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          "& .MuiListItemText-root, .MuiListItemIcon-root, & .MuiButtonBase-root": {
            color: grey[300],
          },

          "& .MuiListItem-root:hover": {
            backgroundColor: "#363636",
          },
          backgroundColor: "#303030",
        },
      },
    },
  },
});

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    lightBlue: true;
    lightPink: true;
    lightGreen: true;
  }
}

interface Props {
  children?: React.ReactNode;
}

export function MuiThemeEA(props: Props) {
  return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
}
